import React from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import SearchBar from 'shared/components/search-bar/search-bar';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import { WanderingBoundary } from 'features/residents/models/wandering-boundary';
import useWanderingBoundaryLinkModal from './hooks';
import styles from './wandering-boundary-link-modal.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLinkButtonClicked: (selectedWanderingBoundaries: WanderingBoundary[]) => void;
  wanderingBoundaryIdsToHide: string[];
};

const WanderingBoundaryLinkModal = ({
  isOpen,
  onClose,
  onLinkButtonClicked,
  wanderingBoundaryIdsToHide,
}: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const {
    selectedWanderingBoundaries,
    updateSelectedWanderingBoundaries,
    handleOnClose,
    handleOnLinkButtonClicked,
    wanderingBoundaries,
    totalWanderingBoundaries,
    readLinkableWanderingBoundariesIsLoading,
    readLinkableWanderingBoundariesIsSuccess,
    currentPage,
    currentRowsPerPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
    searchBarFromSessionStorage,
    sortFromSessionStorage,
    handleOnSearchbarChanged,
  } = useWanderingBoundaryLinkModal({
    isOpen: isOpen,
    onClose: onClose,
    onLinkButtonClicked: onLinkButtonClicked,
    wanderingBoundaryIdsToHide: wanderingBoundaryIdsToHide,
  });

  const columns: ColumnDefinition<WanderingBoundary, NestedKeyof<WanderingBoundary>>[] = [
    {
      key: 'name',
      label: t('linkableWanderingBoundaryModal.table.nameAndDescription'),
      renderCustomContentProvider: (item) => (
        <Stack direction="column">
          <Typography variant="subtitle1">{item.name}</Typography>
          <Typography variant="subtitle1" fontStyle={'italic'}>
            {item.description}
          </Typography>
        </Stack>
      ),
      disableSort: true,
    },
  ];

  return (
    <Dialog open={isOpen} onClose={handleOnClose} slotProps={{ paper: { classes: { root: styles.modalPaper } } }}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <Typography className={styles.headerTitle} variant="h3">
            {t('linkableWanderingBoundaryModal.title')}
          </Typography>
          <div className={styles.headerActionsContainer}>
            {selectedWanderingBoundaries.length < 1 && (
              <Button variant="contained" disabled={true}>
                {t('linkableWanderingBoundaryModal.disabledLinkButtonText')}
              </Button>
            )}
            {selectedWanderingBoundaries.length > 0 && (
              <Button variant="contained" onClick={handleOnLinkButtonClicked} startIcon={<CheckIcon sx={{ width: 16, height: 16 }} />}>
                {t('linkableWanderingBoundaryModal.enabledLinkButtonText', {
                  amountOfSelectedWanderingBoundaries: selectedWanderingBoundaries.length,
                })}
              </Button>
            )}
            <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
              <Close />
            </IconButton>
          </div>
        </div>
        <SearchBar className={styles.searchBar} onSearchValueChanged={handleOnSearchbarChanged} searchValue={searchBarFromSessionStorage} />
        {readLinkableWanderingBoundariesIsLoading && <LoadingIndicator />}
        {readLinkableWanderingBoundariesIsSuccess && wanderingBoundaries && (
          <Table
            className={styles.table}
            data={wanderingBoundaries.map<RowItem<WanderingBoundary>>((wanderingBoundary: WanderingBoundary) => ({
              data: wanderingBoundary,
            }))}
            columns={columns}
            rowsAreSelectable
            onSelectedRowsChanged={updateSelectedWanderingBoundaries}
            rowIdentifier={(item: WanderingBoundary) => item.id}
            selectedRows={selectedWanderingBoundaries.map<RowItem<WanderingBoundary>>((wanderingBoundary: WanderingBoundary) => ({
              data: wanderingBoundary,
            }))}
            onSelectAllRowsChanged={(checked) => updateSelectedWanderingBoundaries(checked ? (wanderingBoundaries ?? []) : [])}
            pagination={{
              count: Math.max(0, (totalWanderingBoundaries ?? 0) - wanderingBoundaryIdsToHide.length),
              page: currentPage,
              rowsPerPage: currentRowsPerPage,
              onPageChanged: handleOnPageChanged,
              onRowsPerPageChanged: handleOnRowsPerPageChanged,
            }}
            onSortChanged={handleOnSortChanged}
            initialOrderBy={sortFromSessionStorage.property}
            initialOrderDirection={sortFromSessionStorage.isAscending ? 'asc' : 'desc'}
          />
        )}
      </div>
    </Dialog>
  );
};

export default WanderingBoundaryLinkModal;
